import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Minus, Plus } from "lucide-react";
import { CellTextPopover, type CellTextPopoverHandle } from "./CellTextPopover";
import { databookColumnWindow } from "./databookColumnWindow";
import { databookRowWindow } from "./databookRowWindow";
import { projectDatabookCellReferences } from "./databookCellReferences";

import type {
  DatabookPreviewBorderSide,
  DatabookPreviewCell,
  DatabookPreviewStyle,
  DatabookSheetPreview,
} from "@/lib/api/databook";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  databookCellSelectionBounds,
  excelColumnName,
  extendDatabookCellSelection,
  isCellInDatabookSelection,
  startDatabookCellSelection,
  type DatabookCellSelection,
  type DatabookCellSelectionBounds,
} from "./databookCellSelection";

interface DatabookSheetGridProps {
  sheet: DatabookSheetPreview;
  filterHeaderRow?: number;
  filterActions?: ReadonlyMap<number, ReactNode>;
  outlineLoading?: boolean;
  selection?: DatabookCellSelection | null;
  cellReferences?: readonly string[];
  onSelectionChange?: (selection: DatabookCellSelection | null) => void;
  onToggleOutlineGroup?: (groupId: string) => void;
  onToggleColumnOutlineGroup?: (groupId: string) => void;
}

const ignoreSelectionChange = () => undefined;
const ignoreOutlineToggle = () => undefined;
const REPORT_TRAILING_COLUMNS = 4;
const REPORT_TRAILING_ROWS = 10;
const NO_CELL_REFERENCES: readonly string[] = [];

const DEFAULT_STYLE: DatabookPreviewStyle = {
  id: 0,
  font: {
    name: null,
    size: null,
    color: null,
    bold: false,
    italic: false,
    underline: false,
  },
  fill: { color: null },
  border: {
    top: { style: null, color: null },
    right: { style: null, color: null },
    bottom: { style: null, color: null },
    left: { style: null, color: null },
  },
  alignment: {
    horizontal: null,
    vertical: null,
    wrap_text: false,
    indent: 0,
  },
  number_format: "General",
};

function coordinate(row: number, column: number): string {
  return `${row}:${column}`;
}

function borderCss(side: DatabookPreviewBorderSide): string | undefined {
  if (!side.style) return undefined;
  const width = side.style === "thick"
    ? 3
    : side.style.startsWith("medium") || side.style === "double"
      ? 2
      : 1;
  const style = side.style === "double"
    ? "double"
    : side.style.includes("dash")
      ? "dashed"
      : side.style === "dotted" || side.style === "hair"
        ? "dotted"
        : "solid";
  return `${width}px ${style} ${side.color ?? "#000000"}`;
}

function horizontalAlignment(
  cell: DatabookPreviewCell | undefined,
  style: DatabookPreviewStyle,
): CSSProperties["textAlign"] {
  const horizontal = style.alignment.horizontal;
  if (horizontal === "center" || horizontal === "centerContinuous") {
    return "center";
  }
  if (horizontal === "right") return "right";
  if (horizontal === "justify" || horizontal === "distributed") {
    return "justify";
  }
  if (horizontal === "left") return "left";
  return cell?.kind === "number" || cell?.kind === "date" ? "right" : "left";
}

function verticalAlignment(
  style: DatabookPreviewStyle,
): CSSProperties["alignItems"] {
  if (style.alignment.vertical === "top") return "flex-start";
  if (style.alignment.vertical === "center") return "center";
  return "flex-end";
}

function canOverflowIntoEmptyCells(
  cell: DatabookPreviewCell | undefined,
  style: DatabookPreviewStyle,
): boolean {
  const horizontal = style.alignment.horizontal;
  return cell?.kind === "string"
    && !style.alignment.wrap_text
    && (horizontal === null || horizontal === "left" || horizontal === "general");
}

function cellCss(
  cell: DatabookPreviewCell | undefined,
  style: DatabookPreviewStyle,
  showGridLines: boolean,
  centerAcrossCells = false,
): CSSProperties {
  const allowTextOverflow = centerAcrossCells || canOverflowIntoEmptyCells(cell, style);
  return {
    alignItems: verticalAlignment(style),
    backgroundColor: style.fill.color ?? "#ffffff",
    borderTop: borderCss(style.border.top),
    borderRight:
      borderCss(style.border.right)
      ?? (showGridLines ? "1px solid #d9d9d9" : undefined),
    borderBottom:
      borderCss(style.border.bottom)
      ?? (showGridLines ? "1px solid #d9d9d9" : undefined),
    borderLeft: borderCss(style.border.left),
    color: style.font.color ?? "#111827",
    display: "flex",
    fontFamily: style.font.name ?? "Arial, sans-serif",
    fontSize: style.font.size ? `${style.font.size}pt` : "10pt",
    fontStyle: style.font.italic ? "italic" : undefined,
    fontWeight: style.font.bold ? 700 : 400,
    justifyContent: centerAcrossCells ? "flex-start" : horizontalAlignment(cell, style) === "right"
      ? "flex-end"
      : horizontalAlignment(cell, style) === "center"
        ? "center"
        : "flex-start",
    minHeight: 0,
    minWidth: 0,
    overflow: allowTextOverflow ? "visible" : "hidden",
    paddingLeft: 4 + style.alignment.indent * 12,
    paddingRight: 4,
    textAlign: horizontalAlignment(cell, style),
    textDecoration: style.font.underline ? "underline" : undefined,
    whiteSpace: style.alignment.wrap_text ? "pre-wrap" : "pre",
    wordBreak: style.alignment.wrap_text ? "break-word" : undefined,
  };
}

function selectionBoxShadow(
  bounds: DatabookCellSelectionBounds,
  row: number,
  column: number,
): string | undefined {
  const edges: string[] = [];
  if (row === bounds.minRow) edges.push("inset 0 2px #217346");
  if (row === bounds.maxRow) edges.push("inset 0 -2px #217346");
  if (column === bounds.minColumn) edges.push("inset 2px 0 #217346");
  if (column === bounds.maxColumn) edges.push("inset -2px 0 #217346");
  return edges.length > 0 ? edges.join(", ") : undefined;
}

interface SheetRowProps {
  row: number;
  selectionBounds: DatabookCellSelectionBounds | null;
  renderCells: (row: number, bounds: DatabookCellSelectionBounds | null) => ReactNode;
}

// Selection changes only affect rows entering/leaving the range or its edges.
// Preserve the cell elements in all other rows, including their measured layout.
const SheetRow = memo(function SheetRow({ row, selectionBounds, renderCells }: SheetRowProps) {
  return <>{renderCells(row, selectionBounds)}</>;
}, (previous, next) => {
  if (previous.row !== next.row || previous.renderCells !== next.renderCells) return false;
  const { row } = next;
  const before = previous.selectionBounds;
  const after = next.selectionBounds;
  const wasSelected = before !== null && row >= before.minRow && row <= before.maxRow;
  const isSelected = after !== null && row >= after.minRow && row <= after.maxRow;
  if (!wasSelected && !isSelected) return true;
  if (!wasSelected || !isSelected) return false;
  return before.minColumn === after.minColumn
    && before.maxColumn === after.maxColumn
    && (row === before.minRow) === (row === after.minRow)
    && (row === before.maxRow) === (row === after.maxRow);
});

export function DatabookSheetGrid({
  sheet,
  filterHeaderRow,
  filterActions,
  outlineLoading = false,
  selection = null,
  cellReferences = NO_CELL_REFERENCES,
  onSelectionChange = ignoreSelectionChange,
  onToggleOutlineGroup = ignoreOutlineToggle,
  onToggleColumnOutlineGroup = ignoreOutlineToggle,
}: DatabookSheetGridProps) {
  const dragging = useRef(false);
  const pointerStart = useRef<{ x: number; y: number; row: number; column: number } | null>(null);
  const cellTextPopover = useRef<CellTextPopoverHandle>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState({ left: 0, width: 1200, top: 0, height: 800 });
  const selectionRef = useRef(selection);
  const selectionBounds = databookCellSelectionBounds(selection, sheet.name);
  const visibleRowOffset = sheet.row_offset ?? 0;
  const visibleRowCount = sheet.returned_row_count ?? sheet.row_count;
  const trailingRowCount = sheet.row_offset === undefined
    ? REPORT_TRAILING_ROWS
    : 0;
  const trailingColumnCount = sheet.row_offset === undefined
    ? REPORT_TRAILING_COLUMNS
    : 0;
  const displayedRowCount = visibleRowCount + trailingRowCount;
  const displayedColumnCount = sheet.column_count + trailingColumnCount;
  const virtualizeRows = sheet.name === "Mapping" && displayedRowCount > 200;
  const referencePositions = useMemo(
    () => projectDatabookCellReferences(sheet, cellReferences),
    [sheet, cellReferences],
  );
  const referencedCells = useMemo(
    () => new Set(referencePositions.map(cell => coordinate(cell.row, cell.column))),
    [referencePositions],
  );
  const scrolledReference = useRef<string | null>(null);

  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  useEffect(() => {
    const stopDragging = () => {
      dragging.current = false;
    };
    window.addEventListener("pointerup", stopDragging);
    window.addEventListener("pointercancel", stopDragging);
    window.addEventListener("blur", stopDragging);
    return () => {
      window.removeEventListener("pointerup", stopDragging);
      window.removeEventListener("pointercancel", stopDragging);
      window.removeEventListener("blur", stopDragging);
    };
  }, []);

  const handlePointerDown = useCallback((
    event: ReactPointerEvent<HTMLDivElement>,
    row: number,
    column: number,
  ) => {
    if (event.button !== 0) return;
    pointerStart.current = { x: event.clientX, y: event.clientY, row, column };
    event.preventDefault();
    dragging.current = true;
    const next = startDatabookCellSelection(
      sheet.name,
      selectionRef.current,
      { row, column },
      event.shiftKey,
    );
    selectionRef.current = next;
    onSelectionChange(next);
  }, [onSelectionChange, sheet.name]);

  const handlePointerEnter = useCallback((row: number, column: number) => {
    if (!dragging.current) return;
    cellTextPopover.current?.close();
    if (!selectionRef.current) return;
    const next = extendDatabookCellSelection(
      selectionRef.current,
      { row, column },
    );
    selectionRef.current = next;
    onSelectionChange(next);
  }, [onSelectionChange]);

  const layout = useMemo(() => {
    const rowHeights = Array.from(
      { length: displayedRowCount },
      () => sheet.default_row_height,
    );
    for (const row of sheet.rows) {
      const localRow = row.index - visibleRowOffset;
      if (localRow >= 0 && localRow < rowHeights.length) {
        rowHeights[localRow] = row.height;
      }
    }
    const columnWidths = Array.from(
      { length: displayedColumnCount },
      () => sheet.default_column_width,
    );
    for (const column of sheet.columns) {
      if (column.index < columnWidths.length) {
        columnWidths[column.index] = column.width;
      }
    }

    const rowOffsets: number[] = [];
    let rowOffset = 0;
    for (const height of rowHeights) {
      rowOffsets.push(rowOffset);
      rowOffset += height;
    }
    const sourceToLocalRow = new Map(
      sheet.rows.map((row) => [row.source_index, row.index - visibleRowOffset]),
    );
    const outlineGroups = sheet.outline_groups.flatMap((group) => {
      const summaryRow = sourceToLocalRow.get(group.summary_source_index);
      if (summaryRow === undefined) return [];
      let firstDetailRow: number | undefined;
      let lastDetailRow: number | undefined;
      for (const row of sheet.rows) {
        if (
          row.source_index < group.start_source_index
          || row.source_index > group.end_source_index
        ) {
          continue;
        }
        const localRow = row.index - visibleRowOffset;
        if (localRow < 0 || localRow >= rowHeights.length) continue;
        firstDetailRow ??= localRow;
        lastDetailRow = localRow;
      }
      return [{ group, firstDetailRow, lastDetailRow, summaryRow }];
    });
    const columnOffsets: number[] = [];
    let columnOffset = 0;
    for (const width of columnWidths) {
      columnOffsets.push(columnOffset);
      columnOffset += width;
    }
    const sourceToProjectedColumn = new Map(
      sheet.columns.map((column) => [column.source_index, column.index]),
    );
    const columnOutlineGroups = sheet.column_outline_groups.flatMap((group) => {
      const summaryColumn = sourceToProjectedColumn.get(group.summary_source_index);
      if (summaryColumn === undefined) return [];
      let firstDetailColumn: number | undefined;
      let lastDetailColumn: number | undefined;
      for (const column of sheet.columns) {
        if (
          column.source_index < group.start_source_index
          || column.source_index > group.end_source_index
        ) {
          continue;
        }
        firstDetailColumn ??= column.index;
        lastDetailColumn = column.index;
      }
      return [{
        group,
        firstDetailColumn,
        lastDetailColumn,
        summaryColumn,
      }];
    });
    const cells = new Map(
      sheet.cells.map((cell) => [coordinate(cell.row, cell.column), cell]),
    );
    const styles = new Map(sheet.styles.map((style) => [style.id, style]));
    const rowSourceIndexes = new Map(
      sheet.rows.map((row) => [row.index, row.source_index]),
    );
    const columnSourceIndexes = new Map(
      sheet.columns.map((column) => [column.index, column.source_index]),
    );
    const mergeAnchors = new Map<string, DatabookSheetPreview["merges"][number]>();
    const mergedChildren = new Set<string>();
    for (const merge of sheet.merges) {
      mergeAnchors.set(coordinate(merge.start_row, merge.start_column), merge);
      for (let row = merge.start_row; row <= merge.end_row; row += 1) {
        for (
          let column = merge.start_column;
          column <= merge.end_column;
          column += 1
        ) {
          if (row !== merge.start_row || column !== merge.start_column) {
            mergedChildren.add(coordinate(row, column));
          }
        }
      }
    }
    const centerAcrossRanges = new Map<string, { start: number; end: number }>();
    for (const cell of sheet.cells) {
      const key = coordinate(cell.row, cell.column);
      const style = styles.get(cell.style_id) ?? DEFAULT_STYLE;
      if (!cell.display_text || style.alignment.horizontal !== "centerContinuous"
        || mergeAnchors.has(key) || mergedChildren.has(key)) continue;
      let end = cell.column;
      for (let column = cell.column + 1; column < sheet.column_count; column += 1) {
        const nextKey = coordinate(cell.row, column);
        const nextCell = cells.get(nextKey);
        const nextStyle = styles.get(nextCell?.style_id ?? 0) ?? DEFAULT_STYLE;
        if (nextCell?.display_text || nextStyle.alignment.horizontal !== "centerContinuous"
          || mergeAnchors.has(nextKey) || mergedChildren.has(nextKey)) break;
        end = column;
      }
      const range = { start: cell.column, end };
      for (let column = range.start; column <= range.end; column += 1) {
        centerAcrossRanges.set(coordinate(cell.row, column), range);
      }
    }
    return {
      cells,
      centerAcrossRanges,
      columnOffsets,
      columnOutlineGroups,
      columnWidths,
      mergeAnchors,
      mergedChildren,
      outlineGroups,
      rowHeights,
      rowOffsets,
      rowSourceIndexes,
      styles,
      totalRowHeight: rowOffset,
      totalColumnWidth: columnOffset,
      columnSourceIndexes,
    };
  }, [displayedColumnCount, displayedRowCount, sheet, visibleRowOffset]);

  const textOverflowWidth = useCallback((
    row: number,
    column: number,
    merge: DatabookSheetPreview["merges"][number] | undefined,
  ): number => {
    const startColumn = column;
    let endColumn = merge?.end_column ?? column;
    for (
      let candidate = endColumn + 1;
      candidate < displayedColumnCount;
      candidate += 1
    ) {
      const candidateKey = coordinate(row, candidate);
      const candidateCell = layout.cells.get(candidateKey);
      if (
        candidateCell?.display_text
        || layout.mergeAnchors.has(candidateKey)
        || layout.mergedChildren.has(candidateKey)
      ) {
        break;
      }
      endColumn = candidate;
    }
    return layout.columnOffsets[endColumn]
      + layout.columnWidths[endColumn]
      - layout.columnOffsets[startColumn];
  }, [displayedColumnCount, layout]);

  const expandedOutlineGroups = new Set(sheet.expanded_outline_groups);
  const expandedColumnOutlineGroups = new Set(
    sheet.expanded_column_outline_groups,
  );
  const outlineDepth = sheet.outline_groups.reduce(
    (maximum, group) => Math.max(maximum, group.level),
    0,
  );
  const outlineWidth = sheet.row_offset === undefined && outlineDepth > 0
    ? outlineDepth * 18 + 8
    : 0;
  const columnOutlineDepth = sheet.column_outline_groups.reduce(
    (maximum, group) => Math.max(maximum, group.level),
    0,
  );
  const columnOutlineHeight = sheet.row_offset === undefined
    && columnOutlineDepth > 0
    ? columnOutlineDepth * 18 + 8
    : 0;
  const canvasWidth = layout.totalColumnWidth;
  const canvasHeight = layout.totalRowHeight;
  const contentTopInset = sheet.default_row_height / 2;
  const availableGridHeight = columnOutlineHeight > 0
    ? `calc(100% - ${columnOutlineHeight}px)`
    : "100%";

  const updateViewport = useCallback(() => {
    const element = viewportRef.current;
    if (!element || (displayedColumnCount <= 80 && !virtualizeRows)) return;
    const left = element.scrollLeft;
    const width = element.clientWidth;
    const top = element.scrollTop;
    const height = element.clientHeight;
    setViewport(previous => previous.left === left && previous.width === width
      && previous.top === top && previous.height === height ? previous : { left, width, top, height });
  }, [displayedColumnCount, virtualizeRows]);

  useEffect(() => {
    const first = referencePositions[0];
    const element = viewportRef.current;
    if (!first || !element) {
      scrolledReference.current = null;
      return;
    }
    const target = `${sheet.name}:${cellReferences[0]}:${first.row}:${first.column}`;
    if (target === scrolledReference.current) return;
    scrolledReference.current = target;
    const localRow = first.row - visibleRowOffset;
    const frozenHeight = layout.rowHeights.slice(0, sheet.frozen_rows).reduce((sum, height) => sum + height, 0);
    const frozenWidth = layout.columnWidths.slice(0, sheet.frozen_columns).reduce((sum, width) => sum + width, 0);
    element.scrollTop = Math.max(0, layout.rowOffsets[localRow] - frozenHeight - 24);
    element.scrollLeft = Math.max(0, layout.columnOffsets[first.column] - frozenWidth - 24);
    updateViewport();
  }, [referencePositions, cellReferences, sheet.name, sheet.frozen_rows, sheet.frozen_columns, visibleRowOffset, layout, updateViewport]);

  useEffect(() => {
    const element = viewportRef.current;
    if (!element || (displayedColumnCount <= 80 && !virtualizeRows)) return;
    updateViewport();
    const observer = new ResizeObserver(updateViewport);
    observer.observe(element);
    return () => observer.disconnect();
  }, [displayedColumnCount, virtualizeRows, updateViewport]);

  const rowWindow = virtualizeRows
    ? databookRowWindow(layout.rowOffsets, viewport.top, viewport.height)
    : { start: 0, end: displayedRowCount - 1 };
  const visibleRows = useMemo(() => {
    const rows = new Set<number>();
    for (let row = rowWindow.start; row <= rowWindow.end; row += 1) rows.add(row);
    for (let row = 0; row < Math.min(sheet.frozen_rows, displayedRowCount); row += 1) rows.add(row);
    if (filterHeaderRow !== undefined) rows.add(filterHeaderRow - visibleRowOffset);
    for (const merge of sheet.merges) {
      if (merge.start_row - visibleRowOffset < rowWindow.start && merge.end_row - visibleRowOffset >= rowWindow.start) {
        rows.add(merge.start_row - visibleRowOffset);
      }
    }
    return [...rows].filter(row => row >= 0 && row < displayedRowCount).sort((a, b) => a - b);
  }, [rowWindow.start, rowWindow.end, sheet.frozen_rows, sheet.merges, displayedRowCount, filterHeaderRow, visibleRowOffset]);

  const columnWindow = databookColumnWindow(
    layout.columnOffsets, layout.columnWidths, viewport.left, Math.max(0, viewport.width - outlineWidth),
  );
  const visibleColumns = useMemo(() => {
    const columns = new Set<number>();
    for (let column = 0; column < Math.min(sheet.frozen_columns, displayedColumnCount); column += 1) columns.add(column);
    for (let column = columnWindow.start; column <= columnWindow.end; column += 1) columns.add(column);
    return [...columns].sort((a, b) => a - b);
  }, [columnWindow.start, columnWindow.end, sheet.frozen_columns, displayedColumnCount]);

  const renderRow = useCallback((row: number, selectionBounds: DatabookCellSelectionBounds | null) => {
    const localRow = row - visibleRowOffset;
    const columns = new Set(visibleColumns);
    // Retain offscreen anchors whose merged or overflowing text reaches the viewport.
    for (const merge of sheet.merges) {
      if (merge.start_row === row && merge.end_column >= columnWindow.start && merge.start_column <= columnWindow.end) {
        columns.add(merge.start_column);
      }
    }
    for (const column of visibleColumns) {
      const centerRange = layout.centerAcrossRanges.get(coordinate(row, column));
      if (centerRange) columns.add(centerRange.start);
    }
    for (let column = columnWindow.start - 1; column >= 0; column -= 1) {
      const key = coordinate(row, column);
      if (layout.mergedChildren.has(key) || layout.mergeAnchors.has(key)) break;
      const cell = layout.cells.get(key);
      if (!cell?.display_text) continue;
      const style = layout.styles.get(cell.style_id) ?? DEFAULT_STYLE;
      if (canOverflowIntoEmptyCells(cell, style)) columns.add(column);
      break;
    }
    return [...columns].sort((a, b) => a - b).map((column) => {
      const key = coordinate(row, column);
      if (layout.mergedChildren.has(key)) return null;
      const cell = layout.cells.get(key);
      const style = layout.styles.get(cell?.style_id ?? 0) ?? DEFAULT_STYLE;
      const filterAction = row === filterHeaderRow ? filterActions?.get(column) : undefined;
      const merge = layout.mergeAnchors.get(key);
      const frozenRow = row < sheet.frozen_rows || row === filterHeaderRow;
      const frozenColumn = column < sheet.frozen_columns;
      const selected = isCellInDatabookSelection(
        selectionBounds,
        row,
        column,
      );
      const referenced = referencedCells.has(key);
      // Show center-across headings as one visual block, retaining the
      // underlying cells for selection and copying.
      const centerRange = layout.centerAcrossRanges.get(key);
      const centerAcrossCells = centerRange?.start === column;
      const allowTextOverflow = !filterAction && (centerAcrossCells || canOverflowIntoEmptyCells(cell, style));
      const availableTextWidth = centerAcrossCells
        ? layout.columnOffsets[centerRange.end] + layout.columnWidths[centerRange.end]
          - layout.columnOffsets[column]
        : allowTextOverflow ? textOverflowWidth(row, column, merge) : undefined;
      const visualStyle = cellCss(cell, style, sheet.show_grid_lines, centerAcrossCells);
      if (centerRange) {
        if (column > centerRange.start) visualStyle.borderLeft = "none";
        if (column < centerRange.end) visualStyle.borderRight = "none";
      }
      const selectionStyle: CSSProperties = {
        backgroundImage: selected
          ? "linear-gradient(rgba(33, 115, 70, 0.14), rgba(33, 115, 70, 0.14))"
          : referenced ? "linear-gradient(rgba(59, 130, 246, 0.18), rgba(59, 130, 246, 0.18))" : undefined,
        boxShadow: selected && selectionBounds
          ? selectionBoxShadow(selectionBounds, row, column)
          : referenced ? "inset 0 0 0 2px rgb(59, 130, 246)" : undefined,
        cursor: "cell",
        userSelect: "none",
      };
      const positionStyle: CSSProperties = {
        gridColumn: merge
          ? `${column + 1} / ${merge.end_column + 2}`
          : column + 1,
        gridRow: merge
          ? `${merge.start_row - visibleRowOffset + 1} / ${
              merge.end_row - visibleRowOffset + 2
            }`
          : localRow + 1,
        position: frozenRow || frozenColumn ? "sticky" : "relative",
        top: frozenRow
          ? columnOutlineHeight + layout.rowOffsets[localRow]
          : undefined,
        left: frozenColumn
          ? outlineWidth + layout.columnOffsets[column]
          : undefined,
        zIndex: frozenRow && frozenColumn
          ? allowTextOverflow ? 7 : 6
          : frozenRow || frozenColumn
            ? allowTextOverflow ? 5 : 4
            : allowTextOverflow
              ? 2
              : 1,
      };
      const cellComment = cell?.comment ?? null;
      const cellAddress = `${excelColumnName(
        layout.columnSourceIndexes.get(column) ?? column,
      )}${(layout.rowSourceIndexes.get(row) ?? row) + 1}`;
      const cellElement = (
        <div
          key={key}
          role="gridcell"
          aria-selected={selected}
          aria-label={cellComment !== null
            ? `${cellAddress} has an Excel comment`
            : undefined}
          aria-description={referenced ? ["Referenced by Q&A", cellComment].filter(Boolean).join(". ") : cellComment ?? undefined}
          data-column={column}
          data-row={row}
          data-referenced={referenced ? "true" : undefined}
          data-cell-comment={cellComment !== null ? "true" : undefined}
          tabIndex={cellComment !== null ? 0 : undefined}
          onPointerDown={(event) => {
            handlePointerDown(event, row, column);
          }}
          onPointerEnter={() => handlePointerEnter(row, column)}
          onPointerUp={() => {
            dragging.current = false;
          }}
          onClick={(event) => {
            const start = pointerStart.current;
            pointerStart.current = null;
            if (event.shiftKey || (start && (
              start.row !== row || start.column !== column
              || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 4
            ))) return;
            cellTextPopover.current?.toggle(event.currentTarget, cellAddress, cell?.display_text ?? "");
          }}
          style={{
            ...visualStyle,
            ...selectionStyle,
            ...positionStyle,
            ...(filterAction ? { paddingRight: 24, overflow: "hidden" } : {}),
          }}
        >
          <span
            data-cell-text
            className={allowTextOverflow
              ? "overflow-hidden"
              : "max-w-full overflow-hidden text-ellipsis"}
            style={allowTextOverflow
              ? {
                  flexShrink: 0,
                  maxWidth: centerAcrossCells ? Math.max(0, availableTextWidth! - 8) : availableTextWidth,
                  width: centerAcrossCells ? Math.max(0, availableTextWidth! - 8) : "max-content",
                  pointerEvents: centerAcrossCells ? "none" : undefined,
                }
              : undefined}
          >
            {cell?.display_text ?? ""}
          </span>
          {filterAction}
          {cellComment !== null && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-0 top-0 h-0 w-0 border-l-[8px] border-t-[8px] border-l-transparent border-t-[#dc2626]"
            />
          )}
        </div>
      );
      return cellComment === null ? cellElement : (
        <Tooltip key={key}>
          <TooltipTrigger asChild>{cellElement}</TooltipTrigger>
          <TooltipContent
            align="start"
            className="max-h-80 w-[min(28rem,calc(100vw-2rem))] overflow-y-auto border-[#d6c67a] bg-[#fffbe6] p-3 text-xs leading-relaxed text-[#1f2937]"
            side="right"
          >
            <p className="mb-2 font-mono text-[11px] font-semibold text-[#6b5d20]">
              {cellAddress}
            </p>
            <p className="whitespace-pre-wrap break-words">
              {cellComment}
            </p>
          </TooltipContent>
        </Tooltip>
      );
    });
  }, [layout, sheet, visibleRowOffset, columnOutlineHeight, outlineWidth, textOverflowWidth, handlePointerDown, handlePointerEnter, visibleColumns, columnWindow.start, columnWindow.end, filterHeaderRow, filterActions, referencedCells]);

  if (visibleRowCount === 0 || sheet.column_count === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        This sheet is empty.
      </div>
    );
  }

  return (
    <>
    <div
      ref={viewportRef}
      className="h-full w-full overflow-auto bg-white"
      data-testid="databook-sheet-grid"
      onScroll={() => {
        cellTextPopover.current?.close();
        updateViewport();
      }}
    >
      <div
        className="flex w-max min-w-full bg-[#f3f3f3]"
        style={{
          minHeight: `max(100%, ${Math.max(
            canvasHeight + columnOutlineHeight + contentTopInset,
            1,
          )}px)`,
        }}
      >
        {outlineWidth > 0 && (
          <div
            aria-label="Row outline"
            className="sticky left-0 z-20 shrink-0 border-r border-[#b7b7b7] bg-[#f3f3f3]"
            style={{ marginTop: columnOutlineHeight, width: outlineWidth }}
          >
            {layout.outlineGroups.map(({
              group,
              firstDetailRow,
              lastDetailRow,
              summaryRow,
            }) => {
              const expanded = expandedOutlineGroups.has(group.id);
              const buttonSize = 16;
              const buttonLeft = (group.level - 1) * 18 + 4;
              const summaryCenter = layout.rowOffsets[summaryRow]
                + layout.rowHeights[summaryRow] / 2;
              const detailStart = firstDetailRow === undefined
                ? summaryCenter
                : layout.rowOffsets[firstDetailRow]
                  + layout.rowHeights[firstDetailRow] / 2;
              const detailEnd = lastDetailRow === undefined
                ? summaryCenter
                : layout.rowOffsets[lastDetailRow]
                  + layout.rowHeights[lastDetailRow] / 2;
              const lineStart = Math.min(summaryCenter, detailStart);
              const lineEnd = Math.max(summaryCenter, detailEnd);
              return (
                <div key={group.id}>
                  {expanded && lineEnd > lineStart && (
                    <span
                      aria-hidden="true"
                      className="absolute border-l border-[#7a7a7a]"
                      style={{
                        height: lineEnd - lineStart,
                        left: buttonLeft + buttonSize / 2,
                        top: lineStart + contentTopInset,
                      }}
                    />
                  )}
                  <button
                    type="button"
                    aria-label={`${expanded ? "Collapse" : "Expand"} row group at row ${group.summary_source_index + 1}`}
                    aria-pressed={expanded}
                    disabled={outlineLoading}
                    onClick={() => onToggleOutlineGroup(group.id)}
                    className="absolute z-10 flex h-4 w-4 items-center justify-center rounded-[2px] border border-[#888] bg-white text-[#303030] shadow-sm hover:bg-[#e8e8e8] disabled:cursor-wait disabled:opacity-60"
                    style={{
                      left: buttonLeft,
                      top: summaryCenter - buttonSize / 2 + contentTopInset,
                    }}
                  >
                    {expanded ? (
                      <Minus aria-hidden="true" size={11} strokeWidth={1.5} />
                    ) : (
                      <Plus aria-hidden="true" size={11} strokeWidth={1.5} />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div
          className="flex min-h-full flex-col bg-white"
          style={{
            minWidth: outlineWidth > 0
              ? `calc(100% - ${outlineWidth}px)`
              : "100%",
          }}
        >
          {columnOutlineHeight > 0 && (
            <div
              aria-label="Column outline"
              className="sticky top-0 z-20 shrink-0 border-b border-[#b7b7b7] bg-[#f3f3f3]"
              style={{
                height: columnOutlineHeight,
                minWidth: "100%",
                width: layout.totalColumnWidth,
              }}
            >
              {layout.columnOutlineGroups.map(({
                group,
                firstDetailColumn,
                lastDetailColumn,
                summaryColumn,
              }) => {
                const expanded = expandedColumnOutlineGroups.has(group.id);
                const buttonSize = 16;
                const buttonTop = (group.level - 1) * 18 + 4;
                const summaryCenter = layout.columnOffsets[summaryColumn]
                  + layout.columnWidths[summaryColumn] / 2;
                const detailStart = firstDetailColumn === undefined
                  ? summaryCenter
                  : layout.columnOffsets[firstDetailColumn]
                    + layout.columnWidths[firstDetailColumn] / 2;
                const detailEnd = lastDetailColumn === undefined
                  ? summaryCenter
                  : layout.columnOffsets[lastDetailColumn]
                    + layout.columnWidths[lastDetailColumn] / 2;
                const lineStart = Math.min(summaryCenter, detailStart);
                const lineEnd = Math.max(summaryCenter, detailEnd);
                return (
                  <div key={group.id}>
                    {expanded && lineEnd > lineStart && (
                      <span
                        aria-hidden="true"
                        className="absolute border-t border-[#7a7a7a]"
                        style={{
                          left: lineStart,
                          top: buttonTop + buttonSize / 2,
                          width: lineEnd - lineStart,
                        }}
                      />
                    )}
                    <button
                      type="button"
                      aria-label={`${expanded ? "Collapse" : "Expand"} column group at column ${excelColumnName(group.summary_source_index)}`}
                      aria-pressed={expanded}
                      disabled={outlineLoading}
                      onClick={() => onToggleColumnOutlineGroup(group.id)}
                      className="absolute z-10 flex h-4 w-4 items-center justify-center rounded-[2px] border border-[#888] bg-white text-[#303030] shadow-sm hover:bg-[#e8e8e8] disabled:cursor-wait disabled:opacity-60"
                      style={{
                        left: summaryCenter - buttonSize / 2,
                        top: buttonTop,
                      }}
                    >
                      {expanded ? (
                        <Minus aria-hidden="true" size={11} strokeWidth={1.5} />
                      ) : (
                        <Plus aria-hidden="true" size={11} strokeWidth={1.5} />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div
            role="grid"
            aria-label={sheet.name}
            aria-multiselectable="true"
            aria-rowcount={displayedRowCount}
            aria-colcount={displayedColumnCount}
            style={{
              display: "grid",
              paddingTop: contentTopInset,
              gridTemplateColumns: layout.columnWidths.map((width) => `${width}px`).join(" "),
              gridTemplateRows: layout.rowHeights.map((height) => `${height}px`).join(" "),
              minHeight: `max(${availableGridHeight}, ${Math.max(canvasHeight + contentTopInset, 1)}px)`,
              minWidth: "100%",
              width: `max(100%, ${Math.max(canvasWidth, 1)}px)`,
            }}
          >
          {visibleRows.map((localRow) => (
            <SheetRow
              key={visibleRowOffset + localRow}
              row={visibleRowOffset + localRow}
              selectionBounds={selectionBounds}
              renderCells={renderRow}
            />
          ))}
        </div>
      </div>
      </div>
    </div>
    <CellTextPopover ref={cellTextPopover} sheet={sheet} />
    </>
  );
}
